/**
 * injectChatColors - Inyecta colores personalizados del chat widget en runtime
 *
 * Este módulo permite sobrescribir los colores del chat widget definidos en
 * tailwind.config.js mediante CSS variables en runtime. Los colores se obtienen
 * desde la configuración global HELLOMATIK_CONFIG.
 *
 * @module injectChatColors
 */

// Re-export shared map so existing consumers (e.g. extension/sidepanel.tsx) keep working
export { COLOR_VAR_MAP, transformColorValue } from '@hellomatik/shared/constants/chatColorVarMap.js';
import { COLOR_VAR_MAP, transformColorValue, normalizeChatColorValue, sanitizeCSSValue } from '@hellomatik/shared/constants/chatColorVarMap.js';
import { hexToRgb, getRelativeLuminance, getContrastRatio } from '@hellomatik/shared/utils/colorUtils.js';

/**
 * Interfaz para la configuración de colores del chat
 * Todos los campos son opcionales. Si no se especifica un color,
 * se usará el valor por defecto definido en frontend/widget/index.css
 */
export interface ChatColorsConfig {
  // Superficies y backgrounds
  surface?: string;           // Background principal del widget
  'user-message'?: string;    // Background mensajes usuario
  'user-message-text'?: string; // Texto mensajes usuario (ej: "#1F2937", "var(--chat-text)")
  input?: string;             // Background input
  hover?: string;             // Hover state

  // Acciones principales
  primary?: string;           // Botón principal
  'primary-hover'?: string;   // Hover botón principal
  'primary-border'?: string;  // border-[1px] botón principal
  'primary-text'?: string;    // Texto botón principal

  // Botones - Variante default (blanco)
  'button-default'?: string;        // Background botón default
  'button-default-hover'?: string;  // Hover botón default
  'button-default-border'?: string; // border-[1px] botón default
  'button-default-text'?: string;   // Texto botón default

  // Botones - Variante back
  'button-back'?: string;        // Background botón back
  'button-back-hover'?: string;  // Hover botón back
  'button-back-border'?: string; // border-[1px] botón back
  'button-back-text'?: string;   // Texto botón back

  // Botones - Variante dark (oscuro)
  'button-dark'?: string;        // Background botón dark
  'button-dark-hover'?: string;  // Hover botón dark
  'button-dark-border'?: string; // border-[1px] botón dark
  'button-dark-text'?: string;   // Texto botón dark

  // Botones - Variante gray
  'button-gray'?: string;        // Background botón gray
  'button-gray-hover'?: string;  // Hover botón gray
  'button-gray-border'?: string; // border-[1px] botón gray
  'button-gray-text'?: string;   // Texto botón gray

  // Bordes
  border?: string;            // Borde por defecto
  'input-border'?: string;    // Borde del input (ej: "1px solid #D3D3D4" o "1.25px solid rgb(206, 210, 170)")

  // Texto
  text?: string;              // Texto principal
  'assistant-text'?: string;  // Texto del asistente
  placeholder?: string;       // Placeholder del input
  'input-text'?: string;      // Texto del input

  // Estados disabled
  disabled?: string;          // Background disabled
  'disabled-text'?: string;   // Texto disabled

  // Loading/typing
  typing?: string;            // Indicador de escritura
  loading?: string;           // Dots de loading

  // Code y markdown
  code?: string;              // Background código
  'code-text'?: string;       // Texto código
  link?: string;              // Enlaces
  'link-hover'?: string;      // Hover enlaces

  // Efectos visuales
  shadow?: string;            // Sombra del popup (ej: "0 4px 10px rgba(0,0,0,0.1)" o "none")
  'input-shadow'?: string;    // Sombra del input
  'input-shadow-hover'?: string; // Sombra del input al hover
  'input-shadow-focus'?: string; // Sombra del input al focus
  'button-shadow'?: string;   // Sombra de los botones

  // border-[1px] radius de componentes
  'button-radius'?: string;           // border-[1px] radius de botones (ej: "8px", "1rem", "0.5rem")
  'button-padding'?: string;          // Padding de botones (ej: "0.75rem 1rem", "12px 16px")
  'assistant-response-radius'?: string;  // border-[1px] radius del contenedor de respuestas del asistente

  // Variantes de botones especiales
  'back-button-variant'?: string;     // Variante del botón volver (ej: "gray", "default", "dark")

  // Contenedor de respuestas del asistente
  'assistant-response-border'?: string; // Color del borde del contenedor de respuestas del asistente (ej: "#D3D3D4")
  'assistant-response-border-width'?: string; // Ancho del borde del contenedor de respuestas del asistente (ej: "1px", "2px", "0" para desactivar)
  'assistant-response-background'?: string; // Color de fondo del contenedor de respuestas del asistente (ej: "#FFFFFF", "transparent")
  'assistant-response-padding'?: string; // Padding del contenedor de respuestas del asistente (ej: "16px", "1rem")

  // Tipografía
  'greeting-font-weight'?: string;    // Font weight del mensaje de bienvenida (ej: "normal", "bold", "600")
  'title-font-family'?: string;       // Font family para títulos: "Inter" o "Hedvig" (por defecto "Hedvig")
  'title-inline'?: string;            // Mostrar título en línea con el texto de presentación: "true" o "false" (por defecto "false")
  'home-show-titles'?: string;        // Mostrar títulos al hacer click en home: "true" o "false" (por defecto "false")
  'font-size'?: string;               // Tamaño de fuente: "small", "default", "large" (por defecto "default")
  'line-height'?: string;             // Line height del texto (ej: "1.5", "1.6", "1.8")

  // Input
  'input-variant'?: string;           // Variante del input (ej: "default", "minified")

  // Typing speed
  'typing-speed'?: string;            // Velocidad de escritura del asistente: "slow", "default", "fast" (por defecto "default")

  // Overlay y popup
  'overlay-background'?: string;      // Background del overlay detrás del popup (default: dynamic color-mix from surface+primary, or custom value like "rgba(0,0,0,0.5)")
  'popup-radius'?: string;            // border-[1px] radius del popup card (ej: "24px", "1.5rem")
  'popup-width'?: string;             // Ancho del popup (ej: "550px", "45vw")
  'popup-height'?: string;            // Alto del popup (ej: "700px", "80vh")

  // Header buttons
  'header-button-background'?: string; // Background de los botones del header (ej: "rgb(206, 211, 170)")
  'header-button-hover'?: string;      // Background hover de los botones del header (ej: "rgb(199, 207, 142)")
  'header-button-icon'?: string;       // Color de los iconos del header (ej: "white", "#FFFFFF")
  'show-layout-toggle'?: string;       // Mostrar botón de cambiar layout: "true" o "false" (por defecto "true")
  'company-logo'?: string;             // URL del logo de la empresa para mostrar en mensajes del asistente (ej: "https://example.com/logo.png")

  // Input button
  'input-button-background'?: string;  // Background del botón de enviar del input (ej: "rgb(206, 211, 170)")
  'input-button-hover'?: string;       // Background hover del botón de enviar (ej: "rgb(199, 207, 142)")
  'input-button-icon'?: string;        // Color del icono del botón de enviar (ej: "white", "#FFFFFF")
  'input-button-disabled'?: string;    // Background del botón desactivado (ej: "rgb(206, 211, 170)")
  'input-button-disabled-icon'?: string; // Color del icono del botón desactivado (ej: "white", "#FFFFFF")

  // Banners
  'disable-legal-banner'?: string;     // Desactivar banner legal: "true" o "false" (por defecto "false")
}

// COLOR_VAR_MAP is now imported from shared/constants/chatColorVarMap
// and re-exported at the top of this file for backward compatibility.

/**
 * Inyecta los colores personalizados del chat en el DOM
 *
 * Lee la configuración de colores desde window.HELLOMATIK_CONFIG['chat-colors']
 * y aplica los colores sobrescribiendo las CSS variables en el elemento #hm-popup
 * 
 * NOTA: Los estilos se guardan en __HELLOMATIK_PENDING_STYLES__ para que el widget
 * los inyecte dentro del Shadow DOM cuando se monte.
 *
 * @example
 * // Los colores se definen en la configuración del core:
 * {
 *   "config_id": "chat-colors",
 *   "config_value": {
 *     "surface": "#F0F0E8",
 *     "primary": "#2A5A42",
 *     "primary-hover": "#1D4432",
 *     "shadow": "0 8px 24px rgba(0,0,0,0.15)"
 *   }
 * }
 *
 * // Para quitar la sombra completamente:
 * {
 *   "config_id": "chat-colors",
 *   "config_value": {
 *     "shadow": "none"
 *   }
 * }
 *
 * // En el loader, después de cargar la configuración:
 * injectChatColors();
 *
 * @returns {void}
 */
// -----------------------------------------------------------------
// PRESET PRIMARY MAP — built-in theme preset IDs › --hm-primary
// Must stay in sync with BUILT_IN_PRESETS in useCustomThemes.tsx
// -----------------------------------------------------------------
const PRESET_PRIMARY_MAP: Record<string, string> = {
  'preset-sand': '#C4704B',
  'preset-noir': '#171717',
  'preset-lavender': '#7C5CFC',
  'preset-rose': '#C9506B',
  'preset-sage': '#588A6B',
  'preset-emerald': '#1C7C54',
  'preset-tropical': '#FF8811',
  'preset-burgundy': '#A71D31',
  'preset-arctic': '#5D737E',
  'preset-obsidian': '#E0E0E0',
  'preset-midnight': '#6E8AFA',
  'preset-ember': '#D4944A',
  'preset-aubergine': '#B392E0',
  'preset-slate': '#3DBDA8',
  'preset-sizzling-sunset': '#800733',
  'preset-glacial-green': '#4D8E80',
  'preset-vintage-red': '#A13842',
  'preset-maroon-oak': '#550D0E',
  'preset-french-toast': '#D48420',
  'preset-tomato-soup': '#C93D21',
};

function parsePossiblyNestedJson( value: unknown, maxDepth: number = 3 ): unknown {
  let parsed = value;

  for( let i = 0; i < maxDepth; i++ ) {
    if( typeof parsed !== 'string' ) break;

    const candidate = parsed.trim();
    if( candidate === '' ) break;

    try {
      parsed = JSON.parse( candidate );
    } catch {
      break;
    }
  }

  return parsed;
}

/**
 * Extract the platform's brand primary color from account-theme config.
 * Checks custom themes (overrides) and built-in presets (PRESET_PRIMARY_MAP).
 * Returns null if no account theme is configured or the default green is active.
 */
function getAccountThemePrimary( config: Record<string, unknown> ): string | null {
  try {
    const raw = config['account-theme'];
    if( !raw ) return null;

    const parsed = parsePossiblyNestedJson( raw );
    if( !parsed || typeof parsed !== 'object' || Array.isArray( parsed ) ) {
      return null;
    }
    const blob = parsed as Record<string, unknown>;

    // Custom theme with overrides
    const activeThemeId = blob.activeThemeId as string | null;
    if( activeThemeId && Array.isArray( blob.customThemes ) ) {
      const theme = ( blob.customThemes as Array<{ id: string; overrides?: Record<string, string> }> )
        .find( t => t.id === activeThemeId );
      if( theme?.overrides?.['--hm-primary'] ) {
        return theme.overrides['--hm-primary'];
      }
    }

    // Built-in preset
    const presetId = blob.activePresetId as string | null;
    if( presetId && PRESET_PRIMARY_MAP[presetId] ) {
      return PRESET_PRIMARY_MAP[presetId];
    }
  } catch {
    // Silently ignore — fallback to widget CSS defaults
  }
  return null;
}

export function injectChatColors(): void {
  try {
    // Obtener la configuración de colores desde la variable global
    const config = ( window as any ).HELLOMATIK_CONFIG;

    if( !config )
      return;

    // Obtener la configuración de colores del chat
    let chatColors = config['chat-colors'] as ChatColorsConfig | undefined;

    // ── Fallback: derive primary from account-theme when chat-colors doesn't set it ──
    const hasPrimary = chatColors && typeof chatColors === 'object' && chatColors.primary;
    if( !hasPrimary ) {
      const brandPrimary = getAccountThemePrimary( config as Record<string, unknown> );
      if( brandPrimary ) {
        if( !chatColors || typeof chatColors !== 'object' ) chatColors = {};
        // SECURITY: sanitize brandPrimary before interpolating into CSS expressions
        const safeBrandPrimary = sanitizeCSSValue( brandPrimary );
        chatColors.primary = safeBrandPrimary;
        // Also set derived colors for consistent CTA appearance
        // Hover: lighten (Apple HIG — buttons "catch light" on hover, not darken)
        const hoverLight = `color-mix(in srgb, ${safeBrandPrimary} 88%, white)`;
        chatColors['primary-hover'] = chatColors['primary-hover'] || hoverLight;
        chatColors['input-button-background'] = chatColors['input-button-background'] || brandPrimary;
        chatColors['input-button-hover'] = chatColors['input-button-hover'] || hoverLight;
      }
    }

    if( !chatColors || typeof chatColors !== 'object' )
      return;

    // ── WCAG contrast validation: auto-fix text colors that clash with backgrounds ──
    // Maps each button-text key to its corresponding background key
    const TEXT_BG_PAIRS: Record<string, string> = {
      'button-back-text':    'button-back',
      'button-default-text': 'button-default',
      'button-dark-text':    'button-dark',
      'button-gray-text':    'button-gray',
      'primary-text':        'primary',
    };

    // Try to parse a hex color from a value (ignoring rgba, color-mix, var() etc.)
    const tryParseHex = ( v: string ): string | null => {
      const trimmed = v.trim();
      if( /^#[0-9A-Fa-f]{3,6}$/.test( trimmed ) ) return trimmed;
      return null;
    };

    // ── Derivar primary-text cuando el tenant NO lo configura (2026-06-13,
    //    caso Clínica Planas: primary #b8be8c pastel + texto blanco por defecto
    //    = contraste 1.9:1 ilegible). Misma fórmula que el auto-fix de abajo —
    //    sin esto, el auto-fix solo corrige pares YA configurados.
    {
      const pVal = normalizeChatColorValue( ( chatColors as Record<string, unknown> )['primary'] );
      const ptVal = normalizeChatColorValue( ( chatColors as Record<string, unknown> )['primary-text'] );
      const pHex = pVal && /^#[0-9A-Fa-f]{3,6}$/.test( pVal.trim() ) ? pVal.trim() : null;
      if( pHex && !ptVal ) {
        const rgb = hexToRgb( pHex );
        if( rgb ) {
          const lum = getRelativeLuminance( rgb.r, rgb.g, rgb.b );
          ( chatColors as Record<string, unknown> )['primary-text'] = lum < 0.5 ? '#FFFFFF' : '#1F2937';
        }
      }
    }

    // For each text-background pair, validate WCAG AA contrast (4.5:1 min)
    // If contrast is insufficient, override the text color
    Object.entries( TEXT_BG_PAIRS ).forEach( ( [textKey, bgKey] ) => {
      const textVal = normalizeChatColorValue( ( chatColors as Record<string, unknown> )[textKey] );
      const bgVal = normalizeChatColorValue( ( chatColors as Record<string, unknown> )[bgKey] );
      if( !textVal || !bgVal ) return;

      const textHex = tryParseHex( textVal );
      const bgHex = tryParseHex( bgVal );
      if( !textHex || !bgHex ) return;

      const textRgb = hexToRgb( textHex );
      const bgRgb = hexToRgb( bgHex );
      if( !textRgb || !bgRgb ) return;

      const textLum = getRelativeLuminance( textRgb.r, textRgb.g, textRgb.b );
      const bgLum = getRelativeLuminance( bgRgb.r, bgRgb.g, bgRgb.b );
      const contrast = getContrastRatio( textLum, bgLum );

      if( contrast < 3 ) {
        // Auto-fix: use white text on dark backgrounds, black on light
        const fixedText = bgLum < 0.5 ? '#FFFFFF' : '#1F2937';
        ( chatColors as Record<string, unknown> )[textKey] = fixedText;
      }
    } );

    // Construir CSS en :host y #hm-popup:
    // - :host cubre elementos fuera del popup (p.ej. overlay fullscreen)
    // - #hm-popup sobrescribe explícitamente los defaults definidos en index.css
    //   (los defaults también viven en #hm-popup, así que heredarlos desde :host no basta)
    let hostRules = '/* Hellomatik - Chat colors */\n:host {\n';
    let popupRules = '#hm-popup {\n';

    Object.entries( chatColors ).forEach( ( [key, rawValue] ) => {
      const value = normalizeChatColorValue( rawValue );
      if( value === null )
        return;

      const cssVarName = COLOR_VAR_MAP[key];
      if( !cssVarName )
        return;

      const finalValue = sanitizeCSSValue( transformColorValue( key, value ) );
      hostRules += `  ${cssVarName}: ${finalValue} !important;\n`;
      popupRules += `  ${cssVarName}: ${finalValue} !important;\n`;
    } );

    // ── Techo de viewport para el alto configurado (2026-08-06) ────────────────
    // `popup-height` viaja como custom property NUMÉRICA (la lee getPopupHeight()
    // con parseFloat, así que no admite min()/calc(): getComputedStyle no resuelve
    // esas funciones en custom properties y devolvería NaN). Por eso el clamp se
    // aplica aquí como max-height REAL del popup, que sí es CSS puro.
    // Defensa en profundidad: AnimationService.getContainerStyles ya pone el mismo
    // techo inline; esto cubre además el instante previo a que React monte.
    if( COLOR_VAR_MAP['popup-height'] && normalizeChatColorValue( ( chatColors as Record<string, unknown> )['popup-height'] ) !== null ) {
      popupRules += `  max-height: calc(100dvh - 48px);\n`;
    }

    // ── Also inject --hm-primary and --hellomatik-contrast-color so the entire
    //    variable chain (--hm-primary › --chat-primary › bg-chat-primary) stays
    //    consistent even when the widget CSS defaults haven't been overridden.
    if( chatColors.primary ) {
      const p = normalizeChatColorValue( chatColors.primary );
      if( p ) {
        const safePrimary = sanitizeCSSValue( p );
        ['--hellomatik-contrast-color', '--hm-primary'].forEach( v => {
          hostRules += `  ${v}: ${safePrimary} !important;\n`;
          popupRules += `  ${v}: ${safePrimary} !important;\n`;
        } );
      }
    }

    // ── Synchronize --hm-primary-text with --chat-primary-text so the
    //    button variant "green" (text-[var(--hm-primary-text)]) and the
    //    input button icon (--chat-input-button-icon) always match.
    //    This is critical after WCAG auto-fix which may change primary-text.
    if( chatColors['primary-text'] ) {
      const pt = normalizeChatColorValue( chatColors['primary-text'] );
      if( pt ) {
        const safePt = sanitizeCSSValue( pt );
        hostRules += `  --hm-primary-text: ${safePt} !important;\n`;
        popupRules += `  --hm-primary-text: ${safePt} !important;\n`;
      }
    }

    hostRules += '}\n';
    popupRules += '}\n';
    const cssRules = `${hostRules}${popupRules}`;

    // Guardar en variable global para que el widget lo inyecte en el Shadow DOM
    if( !( window as any ).__HELLOMATIK_PENDING_STYLES__ )
      ( window as any ).__HELLOMATIK_PENDING_STYLES__ = [];

    ( window as any ).__HELLOMATIK_PENDING_STYLES__.push( cssRules );

  }
  catch( error )
  {
    if( import.meta.env.DEV ) console.error( '[Chat Colors] Failed to apply colors:', error );
  }
}

/**
 * Resetea los colores del chat a los valores por defecto
 * Útil para pruebas o cuando se quiere volver a los colores originales
 *
 * @returns {void}
 */
export function resetChatColors(): void {
  const styleElement = document.getElementById( 'hellomatik-chat-colors' );

  if( styleElement ) {
    styleElement.remove();
  }
}
